import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import {
  collection,
  addDoc,
  deleteDoc,
  query,
  orderBy,
  onSnapshot,
  doc,
  updateDoc,
  where,
  getDocs,
  getDocsFromServer,
} from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { useAuthState } from "react-firebase-hooks/auth";
import CircularProgress from "@mui/material/CircularProgress";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import RestoreIcon from "@mui/icons-material/Restore";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CancelIcon from "@mui/icons-material/Cancel";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import MenuItem from "@mui/material/MenuItem";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Autocomplete from "@mui/material/Autocomplete";
import InputAdornment from "@mui/material/InputAdornment";
import Alert from "@mui/material/Alert";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import GrassIcon from "@mui/icons-material/Grass";
import HistoryIcon from "@mui/icons-material/History";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import PlaylistAddIcon from "@mui/icons-material/PlaylistAdd";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { nanoid } from "nanoid";
import moment from "moment";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  pdf,
} from "@react-pdf/renderer";
// @ts-ignore
import { saveAs } from "file-saver";

import { firebaseAuth } from "../../components/firebase/auth";
import { firebaseStore } from "../../components/firebase/firestore";
import { FreshBatchStatus, freshBatchStatusLabel, getFreshBatchStatus } from "./freshBatchStatus";
import { mergeBatchDateWindow, subscribeRecentBatchesByDate } from "./batchDateLoading";
import "./dashboard.css";
import "./batchWorkspace.css";
import "./freshBatches.css";

interface FreshProduct {
  id: string;
  name_sv: string;
  name_en: string;
  icon: string;
  seedWeight?: number;
  usesCocoHempMat?: boolean;
}

interface FreshProductBatch {
  firebaseId: string;
  productId: string;
  batchId: string;
  seedBagNumber?: string;
  plantDate: number | null;
  plantedBy: string;
  harvestDate: number | null;
  harvestedBy: string;
  trayCount: number | null;
  freshWeight: number | null;
  notes: string;
  createdAt: number;
  canceled?: boolean;
  canceledAt?: number | null;
  canceledBy?: string;
  exception?: boolean;
  exceptionNote?: string;
  traysLeft?: number | null;
}

interface BatchCopyCandidate {
  source: FreshProductBatch;
  copied: Omit<FreshProductBatch, "firebaseId">;
  selected: boolean;
}

interface SelectedProductEntry {
  productId: string;
  trayCount: number;
}

// A plant list covers the whole selected period, independent of list pagination.
async function fetchPlantListBatches(startDate: string, endDate: string): Promise<FreshProductBatch[]> {
  const start = moment(startDate, "YYYY-MM-DD", true).startOf("day").valueOf();
  const endExclusive = moment(endDate, "YYYY-MM-DD", true).startOf("day").add(1, "day").valueOf();
  const snapshot = await getDocsFromServer(query(
    collection(firebaseStore, "products_fresh_batch"),
    where("plantDate", ">=", start),
    where("plantDate", "<", endExclusive),
    orderBy("plantDate", "asc"),
  ));
  const batches: FreshProductBatch[] = [];
  snapshot.forEach((entry) => batches.push({ ...entry.data(), firebaseId: entry.id } as FreshProductBatch));
  return batches;
}

// Staged loading by planting date. The initial query includes the last
// 2 weeks, future plantings and undated records. Each stage fetches older plantings.
// `months` is the upper age bound (in months) of the window being loaded.
const LOAD_STAGES: Array<{ key: string; label: string; months: number | null }> = [
  { key: "month", label: "senaste månaden", months: 1 },
  { key: "threeMonths", label: "senaste 3 månaderna", months: 3 },
  { key: "sixMonths", label: "senaste 6 månaderna", months: 6 },
  { key: "year", label: "senaste året", months: 12 },
  { key: "threeYears", label: "senaste 3 åren", months: 36 },
  { key: "all", label: "allt", months: null },
];

// Age (in ms) of the initial planting-date cutoff.
const INITIAL_CUTOFF_MS = 2 * 7 * 24 * 60 * 60 * 1000;

const FRESH_BATCH_STATUSES: FreshBatchStatus[] = ["active", "future", "exception", "historic", "canceled"];

const STATUS_DESCRIPTIONS: Record<FreshBatchStatus, string> = {
  active: "Pågående odlingar och nyligen skördade batcher",
  future: "Batcher med ett kommande planteringsdatum",
  exception: "Batcher som behöver följas upp",
  historic: "Batcher skördade för mer än en vecka sedan",
  canceled: "Avbrutna odlingar som sparas för spårbarhet",
};

const STATUS_ICONS = {
  active: GrassIcon, future: EventOutlinedIcon, exception: WarningAmberIcon,
  historic: HistoryIcon, canceled: CancelIcon,
};

const batchCountLabel = (count: number) => `${count} ${count === 1 ? "batch" : "batcher"}`;

interface BatchDialogProps {
  id: string;
  open: boolean;
  onClose: () => void;
  title: string;
  description: string;
  children: React.ReactNode;
  actions: React.ReactNode;
  busy?: boolean;
  error?: string;
  wide?: boolean;
}

function FreshBatchDialog({ id, open, onClose, title, description, children, actions, busy, error, wide }: BatchDialogProps) {
  return (
    <Dialog className="FreshDialog BatchDialog FreshBatchDialog" fullWidth maxWidth={wide ? "md" : "sm"}
      open={open} onClose={busy ? undefined : onClose} aria-labelledby={`${id}-title`} aria-describedby={`${id}-description`}>
      <DialogTitle id={`${id}-title`}>
        <span>{title}<span className="BatchDialogSubtitle" id={`${id}-description`}>{description}</span></span>
        <IconButton aria-label="Stäng" onClick={onClose} disabled={busy}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent>{children}</DialogContent>
      {error && <Alert severity="error" className="BatchSaveError">{error}</Alert>}
      <DialogActions>{actions}</DialogActions>
    </Dialog>
  );
}

const PLANTED_BY_OPTIONS = [
  "Ann-Helen",
  "Adrian",
  "Jimmy",
  "Ann-Helen & Adrian",
  "Ann-Helen & Jimmy",
  "Adrian & Jimmy",
  "Ann-Helen, Adrian & Jimmy",
];

function BatchFormSection({ title, children }: { title: string; children: React.ReactNode }) {
  return <fieldset className="BatchFormSection"><legend>{title}</legend>{children}</fieldset>;
}

function BatchPersonField({ label, value, onChange, disabled }: {
  label: string; value: string; onChange: (value: string) => void; disabled?: boolean;
}) {
  return <Autocomplete freeSolo options={PLANTED_BY_OPTIONS} inputValue={value}
    onInputChange={(_event, next) => onChange(next)} disabled={disabled}
    renderInput={(params) => <TextField {...params} label={label} size="small" fullWidth />} />;
}

function BatchDateField({ label, value, onChange, disabled, error, helperText }: {
  label: string; value: string; onChange: (value: string) => void; disabled?: boolean;
  error?: boolean; helperText?: string;
}) {
  return <TextField label={label} type="date" size="small" fullWidth value={value}
    onChange={(event) => onChange(event.target.value)} disabled={disabled}
    InputLabelProps={{ shrink: true }} error={error} helperText={helperText} />;
}

interface PlantListPdfProps {
  startDate: string;
  endDate: string;
  batches: FreshProductBatch[];
  getProductName: (productId: string) => string;
  getSeedWeight: (productId: string) => number | null;
  usesCocoHempMat: (productId: string) => boolean;
}

const plantListPdfStyles = StyleSheet.create({
  page: {
    padding: 24,
    backgroundColor: "#ffffff",
    fontFamily: "Helvetica",
    fontSize: 8,
  },
  title: {
    marginBottom: 4,
    color: "#18312b",
    fontFamily: "Helvetica-Bold",
    fontSize: 16,
  },
  subtitle: {
    marginBottom: 12,
    color: "#555555",
    fontSize: 8,
  },
  table: {
    width: "100%",
    borderTop: "1px solid #222222",
    borderLeft: "1px solid #222222",
  },
  row: {
    display: "flex",
    flexDirection: "row",
    width: "100%",
    minHeight: 30,
  },
  headerRow: {
    minHeight: 38,
  },
  cell: {
    display: "flex",
    justifyContent: "center",
    padding: 4,
    borderRight: "1px solid #222222",
    borderBottom: "1px solid #222222",
    textAlign: "left",
  },
  productCell: {
    width: 112,
  },
  batchCell: {
    width: 94,
  },
  dateCell: {
    width: 65,
    textAlign: "center",
  },
  personCell: {
    flexGrow: 1,
    minWidth: 76,
  },
  numberCell: {
    width: 72,
    textAlign: "center",
  },
  notesCell: {
    flexGrow: 1.2,
    minWidth: 80,
  },
  headerCell: {
    alignItems: "center",
    backgroundColor: "#f0eee3",
    fontFamily: "Helvetica-Bold",
    textAlign: "center",
  },
  totalRow: {
    minHeight: 28,
    fontFamily: "Helvetica-Bold",
  },
  footer: {
    marginTop: 10,
    color: "#555555",
    fontSize: 7,
  },
});

const PlantListPdf = ({ startDate, endDate, batches, getProductName, getSeedWeight, usesCocoHempMat }: PlantListPdfProps) => {
  const formatPlantDate = (value: number | null): string => {
    if (!value) return "-";
    const date = moment(value);
    const weekdays = ["söndag", "måndag", "tisdag", "onsdag", "torsdag", "fredag", "lördag"];
    return `${weekdays[date.day()]} ${date.format("YYYY-MM-DD")}`;
  };
  const totalTrays = batches.reduce((sum, batch) => sum + (batch.trayCount || 0), 0);
  const totalCocoHempMats = batches.reduce((sum, batch) => sum + (usesCocoHempMat(batch.productId) ? (batch.trayCount || 0) : 0), 0);
  const getTotalSeedWeight = (batch: FreshProductBatch): number | null => {
    const seedWeight = getSeedWeight(batch.productId);
    return seedWeight !== null && batch.trayCount !== null ? seedWeight * batch.trayCount : null;
  };

  return (
    <Document>
      <Page size="A4" orientation="landscape" style={plantListPdfStyles.page}>
        <Text style={plantListPdfStyles.title}>Plantlista</Text>
        <Text style={plantListPdfStyles.subtitle}>
          Planteringsdatum {startDate} till {endDate} · {batches.length} {batches.length === 1 ? "batch" : "batcher"}
        </Text>
        <View style={plantListPdfStyles.table}>
          <View style={{ ...plantListPdfStyles.row, ...plantListPdfStyles.headerRow }} wrap={false}>
            <Text style={{ ...plantListPdfStyles.cell, ...plantListPdfStyles.productCell, ...plantListPdfStyles.headerCell }}>Produkt</Text>
            <Text style={{ ...plantListPdfStyles.cell, ...plantListPdfStyles.batchCell, ...plantListPdfStyles.headerCell }}>Batch</Text>
            <Text style={{ ...plantListPdfStyles.cell, ...plantListPdfStyles.dateCell, ...plantListPdfStyles.headerCell }}>Sådd</Text>
            <Text style={{ ...plantListPdfStyles.cell, ...plantListPdfStyles.personCell, ...plantListPdfStyles.headerCell }}>Planterad av</Text>
            <Text style={{ ...plantListPdfStyles.cell, ...plantListPdfStyles.numberCell, ...plantListPdfStyles.headerCell }}>Brickor</Text>
            <Text style={{ ...plantListPdfStyles.cell, ...plantListPdfStyles.numberCell, ...plantListPdfStyles.headerCell }}>Coco-/hampmattor</Text>
            <Text style={{ ...plantListPdfStyles.cell, ...plantListPdfStyles.numberCell, ...plantListPdfStyles.headerCell }}>Avvikande antal fat</Text>
            <Text style={{ ...plantListPdfStyles.cell, ...plantListPdfStyles.numberCell, ...plantListPdfStyles.headerCell }}>Frövikt</Text>
            <Text style={{ ...plantListPdfStyles.cell, ...plantListPdfStyles.numberCell, ...plantListPdfStyles.headerCell }}>Total Frövikt</Text>
            <Text style={{ ...plantListPdfStyles.cell, ...plantListPdfStyles.numberCell, ...plantListPdfStyles.headerCell }}>Avvikande Frövikt</Text>
            <Text style={{ ...plantListPdfStyles.cell, ...plantListPdfStyles.notesCell, ...plantListPdfStyles.headerCell }}>Anteckningar</Text>
          </View>
          {batches.map((batch) => (
            <View style={plantListPdfStyles.row} wrap={false} key={batch.firebaseId}>
              <Text style={{ ...plantListPdfStyles.cell, ...plantListPdfStyles.productCell }}>{getProductName(batch.productId)}</Text>
              <Text style={{ ...plantListPdfStyles.cell, ...plantListPdfStyles.batchCell }}>{batch.batchId}</Text>
              <Text style={{ ...plantListPdfStyles.cell, ...plantListPdfStyles.dateCell }}>{formatPlantDate(batch.plantDate)}</Text>
              <Text style={{ ...plantListPdfStyles.cell, ...plantListPdfStyles.personCell }}>{batch.plantedBy || "-"}</Text>
              <Text style={{ ...plantListPdfStyles.cell, ...plantListPdfStyles.numberCell }}>{batch.trayCount || "-"}</Text>
              <Text style={{ ...plantListPdfStyles.cell, ...plantListPdfStyles.numberCell }}>{usesCocoHempMat(batch.productId) ? (batch.trayCount || "-") : "-"}</Text>
              <Text style={{ ...plantListPdfStyles.cell, ...plantListPdfStyles.numberCell }}> </Text>
              <Text style={{ ...plantListPdfStyles.cell, ...plantListPdfStyles.numberCell }}>{getSeedWeight(batch.productId) ? `${getSeedWeight(batch.productId)}g` : "-"}</Text>
              <Text style={{ ...plantListPdfStyles.cell, ...plantListPdfStyles.numberCell }}>{getTotalSeedWeight(batch) !== null ? `${getTotalSeedWeight(batch)}g` : "-"}</Text>
              <Text style={{ ...plantListPdfStyles.cell, ...plantListPdfStyles.numberCell }}> </Text>
              <Text style={{ ...plantListPdfStyles.cell, ...plantListPdfStyles.notesCell }}>{batch.notes || ""}</Text>
            </View>
          ))}
          <View style={{ ...plantListPdfStyles.row, ...plantListPdfStyles.totalRow }} wrap={false}>
            <Text style={{ ...plantListPdfStyles.cell, ...plantListPdfStyles.productCell }}>Summa</Text>
            <Text style={{ ...plantListPdfStyles.cell, ...plantListPdfStyles.batchCell }}> </Text>
            <Text style={{ ...plantListPdfStyles.cell, ...plantListPdfStyles.dateCell }}> </Text>
            <Text style={{ ...plantListPdfStyles.cell, ...plantListPdfStyles.personCell }}> </Text>
            <Text style={{ ...plantListPdfStyles.cell, ...plantListPdfStyles.numberCell }}>{totalTrays || "-"}</Text>
            <Text style={{ ...plantListPdfStyles.cell, ...plantListPdfStyles.numberCell }}>{totalCocoHempMats || "-"}</Text>
            <Text style={{ ...plantListPdfStyles.cell, ...plantListPdfStyles.numberCell }}> </Text>
            <Text style={{ ...plantListPdfStyles.cell, ...plantListPdfStyles.numberCell }}> </Text>
            <Text style={{ ...plantListPdfStyles.cell, ...plantListPdfStyles.numberCell }}> </Text>
            <Text style={{ ...plantListPdfStyles.cell, ...plantListPdfStyles.numberCell }}> </Text>
            <Text style={{ ...plantListPdfStyles.cell, ...plantListPdfStyles.notesCell }}> </Text>
          </View>
        </View>
        <Text style={plantListPdfStyles.footer}>VERT.farm · Plantlista skapad {moment().format("YYYY-MM-DD HH:mm")}</Text>
      </Page>
    </Document>
  );
};

function DashBoardFreshBatches() {
  const [user, loading] = useAuthState(firebaseAuth);
  const navigate = useNavigate();

  // --- List state ---
  // Batches are stored per status group. Each group is lazily loaded from
  // Firestore the first time its dropdown is opened, and the listener stays
  // active afterwards.
  const [batchesByStatus, setBatchesByStatus] = useState<Record<FreshBatchStatus, FreshProductBatch[]>>({
    active: [],
    historic: [],
    future: [],
    canceled: [],
    exception: [],
  });
  const [products, setProducts] = useState<FreshProduct[]>([]);
  const [sortBy, setSortBy] = useState<"parent" | "planted" | "harvested" | "created">("created");

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<FreshBatchStatus | "all">("all");
  const [loadErrors, setLoadErrors] = useState<Partial<Record<FreshBatchStatus, string>>>({});
  const loadAnchor = useRef(Date.now());
  const [singleError, setSingleError] = useState("");
  const [multiError, setMultiError] = useState("");
  const [copyError, setCopyError] = useState("");
  const [plantListError, setPlantListError] = useState("");
  const [productSearch, setProductSearch] = useState("");
  const [batchAction, setBatchAction] = useState<{ kind: "cancel" | "restore" | "delete"; batch: FreshProductBatch } | null>(null);
  const [actionOpen, setActionOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState("");

  // --- Dropdown / lazy-loading state ---
  // Which groups are currently expanded. Active is open by default.
  const [expandedStatuses, setExpandedStatuses] = useState<Record<FreshBatchStatus, boolean>>({
    active: true,
    historic: false,
    future: false,
    canceled: false,
    exception: false,
  });
  // Whether a group has been opened at least once (so its listener is active).
  const [loadedStatuses, setLoadedStatuses] = useState<Record<FreshBatchStatus, boolean>>({
    active: true,
    historic: true,
    future: true,
    canceled: true,
    exception: true,
  });
  // Per-group loading flags for the initial fetch.
  const [loadingByStatus, setLoadingByStatus] = useState<Record<FreshBatchStatus, boolean>>({
    active: true,
    historic: true,
    future: true,
    canceled: true,
    exception: true,
  });
  // Which load stage has been reached for each status group.
  const [loadStageByStatus, setLoadStageByStatus] = useState<Record<FreshBatchStatus, number>>({
    active: 0,
    historic: 0,
    future: 0,
    canceled: 0,
    exception: 0,
  });
  const [loadingMoreByStatus, setLoadingMoreByStatus] = useState<Record<FreshBatchStatus, boolean>>({
    active: false,
    historic: false,
    future: false,
    canceled: false,
    exception: false,
  });
  // Holds the active unsubscribe functions per group so listeners persist.
  const unsubscribeRefs = useRef<Partial<Record<FreshBatchStatus, () => void>>>({});

  // --- Info dialog ---
  const [infoDialogOpen, setInfoDialogOpen] = useState(false);

  // --- Single batch edit dialog ---
  const [open, setOpen] = useState(false);
  const [editingBatch, setEditingBatch] = useState<FreshProductBatch | null>(null);
  const [dialogLoading, setDialogLoading] = useState(false);

  // --- Multi-batch add dialog ---
  const [multiOpen, setMultiOpen] = useState(false);
  const [multiDialogLoading, setMultiDialogLoading] = useState(false);

  // Form fields for single edit
  const [singleSelectedProduct, setSingleSelectedProduct] = useState<FreshProduct | null>(null);
  const [singleSeedBagNumber, setSingleSeedBagNumber] = useState("");
  const [singlePlantDate, setSinglePlantDate] = useState("");
  const [singlePlantedBy, setSinglePlantedBy] = useState("");
  const [singleHarvestDate, setSingleHarvestDate] = useState("");
  const [singleHarvestedBy, setSingleHarvestedBy] = useState("");
  const [singleTrayCount, setSingleTrayCount] = useState<number | null>(null);
  const [singleFreshWeight, setSingleFreshWeight] = useState<number | null>(null);
  const [singleNotes, setSingleNotes] = useState("");
  const [singleException, setSingleException] = useState(false);
  const [singleExceptionNote, setSingleExceptionNote] = useState("");
  const [singleTraysLeft, setSingleTraysLeft] = useState<number | null>(null);
  const [singleCanceled, setSingleCanceled] = useState(false);

  // Form fields for multi-batch add
  const [multiPlantDate, setMultiPlantDate] = useState("");
  const [multiPlantedBy, setMultiPlantedBy] = useState("");
  const [multiHarvestDate, setMultiHarvestDate] = useState("");
  const [multiHarvestedBy, setMultiHarvestedBy] = useState("");
  const [selectedProducts, setSelectedProducts] = useState<SelectedProductEntry[]>([]);

  // --- Plant list PDF dialog ---
  const [plantListOpen, setPlantListOpen] = useState(false);
  const [plantListStartDate, setPlantListStartDate] = useState("");
  const [plantListEndDate, setPlantListEndDate] = useState("");
  const [plantListLoading, setPlantListLoading] = useState(false);
  const [plantListFetching, setPlantListFetching] = useState(false);
  const [plantListReload, setPlantListReload] = useState(0);
  const [plantListResult, setPlantListResult] = useState<{
    startDate: string; endDate: string; batches: FreshProductBatch[];
  } | null>(null);

  useEffect(() => {
    setPlantListResult(null);
    setPlantListFetching(false);
    setPlantListError("");
    if (!plantListOpen) return;
    const start = moment(plantListStartDate, "YYYY-MM-DD", true);
    const end = moment(plantListEndDate, "YYYY-MM-DD", true);
    if (!start.isValid() || !end.isValid() || start.isAfter(end)) return;

    let canceled = false;
    setPlantListFetching(true);
    fetchPlantListBatches(plantListStartDate, plantListEndDate)
      .then((batches) => {
        if (!canceled) setPlantListResult({ startDate: plantListStartDate, endDate: plantListEndDate, batches });
      })
      .catch((error) => {
        if (canceled) return;
        console.error("Error loading plant list batches:", error);
        setPlantListError("Batcherna för perioden kunde inte hämtas. Försök igen.");
      })
      .finally(() => { if (!canceled) setPlantListFetching(false); });
    return () => { canceled = true; };
  }, [plantListOpen, plantListStartDate, plantListEndDate, plantListReload]);

  // --- Historical batch copy dialogs ---
  const [copyOpen, setCopyOpen] = useState(false);
  const [copyConfirmOpen, setCopyConfirmOpen] = useState(false);
  const [copyStartDate, setCopyStartDate] = useState("");
  const [copyEndDate, setCopyEndDate] = useState("");
  const [copyPasteStartDate, setCopyPasteStartDate] = useState("");
  const [copyCandidates, setCopyCandidates] = useState<BatchCopyCandidate[]>([]);
  const [copyLoading, setCopyLoading] = useState(false);

  // Listen to products_fresh
  useEffect(() => {
    const q = query(collection(firebaseStore, "products_fresh"), orderBy("name_sv"));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const temp: FreshProduct[] = [];
      snapshot.forEach((doc) => {
        const data = doc.data();
        temp.push({ id: doc.id, name_sv: data.name_sv || "", name_en: data.name_en || "", icon: data.icon || "", seedWeight: typeof data.seedWeight === "number" ? data.seedWeight : undefined, usesCocoHempMat: data.usesCocoHempMat === true });
      });
      setProducts(temp);
    });
    return () => unsubscribe();
  }, []);

  // Load by planting date, regardless of when a batch was entered in Firestore.
  const startGroupListener = useCallback((status: FreshBatchStatus) => {
    if (unsubscribeRefs.current[status]) return; // already listening
    const twoWeeksAgo = loadAnchor.current - INITIAL_CUTOFF_MS;
    setLoadingByStatus((prev) => ({ ...prev, [status]: true }));
    setLoadErrors((prev) => ({ ...prev, [status]: "" }));
    const unsubscribe = subscribeRecentBatchesByDate<FreshProductBatch>(
      collection(firebaseStore, "products_fresh_batch"), "plantDate", twoWeeksAgo,
      (live) => {
        // Recent live updates must not remove history explicitly loaded by the user.
        setBatchesByStatus((prev) => ({ ...prev, [status]: mergeBatchDateWindow(
          prev[status], live, "plantDate", twoWeeksAgo, (batch) => getFreshBatchStatus(batch) === status,
        ) }));
        setLoadingByStatus((prev) => ({ ...prev, [status]: false }));
      },
      (error) => {
        console.error("Error loading fresh batches:", error);
        setLoadErrors((prev) => ({ ...prev, [status]: "Batcherna kunde inte hämtas. Försök igen." }));
        setLoadingByStatus((prev) => ({ ...prev, [status]: false }));
        delete unsubscribeRefs.current[status];
      },
    );
    unsubscribeRefs.current[status] = unsubscribe;
  }, []);

  // Toggle a group's dropdown. Opening a group for the first time starts its
  // listener; closing it keeps the listener active so it isn't re-fetched.
  const openStatus = (status: FreshBatchStatus) => {
    setExpandedStatuses((prev) => ({ ...prev, [status]: true }));
    setLoadedStatuses((prev) => ({ ...prev, [status]: true }));
    startGroupListener(status);
  };

  const selectStatus = (status: FreshBatchStatus | "all") => {
    setStatusFilter(status);
    if (status !== "all") openStatus(status);
  };

  const handleToggleGroup = (status: FreshBatchStatus) => {
    if (!expandedStatuses[status]) openStatus(status);
    else setExpandedStatuses((prev) => ({ ...prev, [status]: false }));
  };

  const syncBatchLocally = (batch: FreshProductBatch) => {
    const nextStatus = getFreshBatchStatus(batch);
    setBatchesByStatus((prev) => Object.fromEntries(FRESH_BATCH_STATUSES.map((status) => [
      status,
      [...prev[status].filter((item) => item.firebaseId !== batch.firebaseId), ...(status === nextStatus ? [batch] : [])],
    ])) as Record<FreshBatchStatus, FreshProductBatch[]>);
  };

  const showSavedBatch = (batch: FreshProductBatch) => {
    syncBatchLocally(batch);
    setSearch("");
    selectStatus(getFreshBatchStatus(batch));
  };

  // Clean up all listeners on unmount.
  useEffect(() => {
    return () => {
      Object.values(unsubscribeRefs.current).forEach((unsub) => unsub && unsub());
      unsubscribeRefs.current = {};
    };
  }, []);

  // All groups load their docs when the tab is opened. The active group is
  // open by default; the others stay collapsed but their listeners are
  // started on mount so their lists are loaded and stay up to date.
  useEffect(() => {
    FRESH_BATCH_STATUSES.forEach((status) => startGroupListener(status));
  }, [startGroupListener]);

  // Load more (older) batches for a given status group, advancing one stage.
  const handleLoadMore = async (status: FreshBatchStatus) => {
    const currentStage = loadStageByStatus[status];
    if (currentStage >= LOAD_STAGES.length) return; // all loaded
    const stage = LOAD_STAGES[currentStage];
    setLoadingMoreByStatus((prev) => ({ ...prev, [status]: true }));
    setLoadErrors((prev) => ({ ...prev, [status]: "" }));
    try {
      const now = moment(loadAnchor.current);
      // Upper bound of this window: the previous stage's cutoff.
      // - Stage 0 loads docs older than 2 weeks up to 1 month old.
      // - Later stages load from the previous stage's month limit up to this
      //   stage's month limit (e.g. 1 month → 3 months).
      const upperBound = currentStage === 0
        ? now.clone().subtract(INITIAL_CUTOFF_MS, "ms").valueOf()
        : now.clone().subtract(LOAD_STAGES[currentStage - 1].months!, "months").valueOf();
      // Lower bound: if this stage has a month limit, load docs at least that
      // old; otherwise (all) there is no lower bound.
      const lowerBound = stage.months !== null
        ? now.clone().subtract(stage.months, "months").valueOf()
        : 0;

      const q = query(
        collection(firebaseStore, "products_fresh_batch"),
        where("plantDate", ">=", lowerBound),
        where("plantDate", "<", upperBound),
        orderBy("plantDate", "desc")
      );
      const snapshot = await getDocs(q);
      const older: FreshProductBatch[] = [];
      snapshot.forEach((doc) => {
        const batch = { firebaseId: doc.id, ...doc.data() } as FreshProductBatch;
        if (getFreshBatchStatus(batch) === status) older.push(batch);
      });
      setBatchesByStatus((prev) => {
        const existing = new Set(prev[status].map((b) => b.firebaseId));
        const merged = [...prev[status], ...older.filter((b) => !existing.has(b.firebaseId))];
        return { ...prev, [status]: merged };
      });
      setLoadStageByStatus((prev) => ({ ...prev, [status]: currentStage + 1 }));
    } catch (error) {
      console.error("Error loading more batches:", error);
      setLoadErrors((prev) => ({ ...prev, [status]: "Äldre batcher kunde inte hämtas. Försök igen." }));
    } finally {
      setLoadingMoreByStatus((prev) => ({ ...prev, [status]: false }));
    }
  };

  const getNextLoadStage = (status: FreshBatchStatus): { label: string; isLast: boolean } | null => {
    const currentStage = loadStageByStatus[status];
    if (currentStage >= LOAD_STAGES.length) return null;
    const stage = LOAD_STAGES[currentStage];
    return { label: stage.label, isLast: stage.months === null };
  };

  useEffect(() => { if (user === null && loading === false) navigate("/login"); }, [user, loading, navigate]);

  const getProductName = (productId: string): string => {
    const p = products.find((x) => x.id === productId);
    return p ? `${p.name_sv}${p.name_en ? ` / ${p.name_en}` : ""}` : productId;
  };

  const getSeedWeight = (productId: string): number | null => {
    const product = products.find((item) => item.id === productId);
    return typeof product?.seedWeight === "number" ? product.seedWeight : null;
  };

  const usesCocoHempMat = (productId: string): boolean =>
    products.find((item) => item.id === productId)?.usesCocoHempMat === true;

  // Combined batches across all loaded groups (used for copying).
  const allBatches = useMemo(
    () => Array.from(new Map(FRESH_BATCH_STATUSES.flatMap((status) => batchesByStatus[status]).map((batch) => [batch.firebaseId, batch])).values()),
    [batchesByStatus]
  );

  const getSortedBatches = (status: FreshBatchStatus): FreshProductBatch[] => {
    const sorted = [...batchesByStatus[status]];
    if (sortBy === "parent") {
      sorted.sort((a, b) => getProductName(a.productId).localeCompare(getProductName(b.productId), "sv-SE"));
    } else if (sortBy === "planted") {
      sorted.sort((a, b) => (a.plantDate || 0) - (b.plantDate || 0));
    } else if (sortBy === "harvested") {
      sorted.sort((a, b) => (a.harvestDate || 0) - (b.harvestDate || 0));
    } else {
      sorted.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
    }
    return sorted;
  };

  const searchTerm = search.trim().toLocaleLowerCase("sv-SE");
  const matchesSearch = (batch: FreshProductBatch) => [
    batch.batchId, getProductName(batch.productId), batch.seedBagNumber,
    batch.plantedBy, batch.harvestedBy, batch.notes, batch.exceptionNote,
  ].some((value) => value?.toLocaleLowerCase("sv-SE").includes(searchTerm));

  const clearFilters = () => { setSearch(""); setStatusFilter("all"); };

  const sortPlantListBatches = (batches: FreshProductBatch[]): FreshProductBatch[] =>
    [...batches]
      .sort((a, b) => (a.plantDate || 0) - (b.plantDate || 0) || getProductName(a.productId).localeCompare(getProductName(b.productId), "sv-SE"));

  const handleOpenPlantList = () => {
    setPlantListError("");
    const today = moment().format("YYYY-MM-DD");
    setPlantListStartDate(today);
    setPlantListEndDate(today);
    setPlantListOpen(true);
  };

  const handleExportPlantList = async () => {
    if (plantListLoading) return;
    const start = moment(plantListStartDate, "YYYY-MM-DD", true);
    const end = moment(plantListEndDate, "YYYY-MM-DD", true);
    if (!start.isValid() || !end.isValid()) {
      setPlantListError("Välj start- och slutdatum.");
      return;
    }
    if (start.isAfter(end)) {
      setPlantListError("Startdatum måste vara före eller samma som slutdatum.");
      return;
    }

    setPlantListError("");
    setPlantListLoading(true);
    try {
      // Refresh on export so newly added, changed or deleted batches are reflected.
      const selectedBatches = sortPlantListBatches(await fetchPlantListBatches(plantListStartDate, plantListEndDate));
      setPlantListResult({ startDate: plantListStartDate, endDate: plantListEndDate, batches: selectedBatches });
      if (selectedBatches.length === 0) {
        setPlantListError("Inga batcher har planteringsdatum inom intervallet.");
        return;
      }
      const document = (
        <PlantListPdf
          startDate={plantListStartDate}
          endDate={plantListEndDate}
          batches={selectedBatches}
          getProductName={getProductName}
          getSeedWeight={getSeedWeight}
          usesCocoHempMat={usesCocoHempMat}
        />
      );
      const blob = await pdf(document).toBlob();
      saveAs(blob, `VERTfarm--plantlista--${plantListStartDate}-${plantListEndDate}.pdf`);
      setPlantListOpen(false);
    } catch (error) {
      console.error("Error exporting plant list PDF:", error);
      setPlantListError("Plantlistan kunde inte exporteras. Försök igen.");
    } finally {
      setPlantListLoading(false);
    }
  };

  const handleOpenCopy = () => {
    setCopyError("");
    setCopyStartDate("");
    setCopyEndDate("");
    setCopyPasteStartDate("");
    setCopyCandidates([]);
    setCopyOpen(true);
  };

  const handlePrepareCopy = () => {
    if (!copyStartDate || !copyEndDate || !copyPasteStartDate) {
      setCopyError("Välj ett datumintervall och den nya periodens startdatum.");
      return;
    }
    const sourceStart = moment(copyStartDate).startOf("day");
    const sourceEnd = moment(copyEndDate).endOf("day");
    const pasteStart = moment(copyPasteStartDate).startOf("day");
    if (sourceStart.isAfter(sourceEnd)) {
      setCopyError("Slutdatum måste vara samma som eller efter startdatum.");
      return;
    }

    const candidates = allBatches
      .filter((batch) => batch.plantDate !== null && moment(batch.plantDate).isBetween(sourceStart, sourceEnd, undefined, "[]"))
      .sort((a, b) => (a.plantDate || 0) - (b.plantDate || 0))
      .map((source) => {
        const dayOffset = moment(source.plantDate).startOf("day").diff(sourceStart, "days");
        const plantDate = pasteStart.clone().add(dayOffset, "days").valueOf();
        const harvestDuration = source.plantDate && source.harvestDate
          ? source.harvestDate - source.plantDate
          : null;
        const product = products.find((item) => item.id === source.productId);
        const prefix = (product?.name_sv || source.productId)
          .normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^A-Za-z0-9]/g, "").slice(0, 3).toUpperCase();
        return {
          source,
          selected: true,
          copied: {
            productId: source.productId,
            batchId: `${prefix}-${moment(plantDate).format("YYMMDD")}-${nanoid(4).toUpperCase()}`,
            seedBagNumber: source.seedBagNumber || "",
            plantDate,
            plantedBy: source.plantedBy || "",
            harvestDate: harvestDuration !== null ? plantDate + harvestDuration : null,
            harvestedBy: source.harvestedBy || "",
            trayCount: source.trayCount || 0,
            freshWeight: source.freshWeight || 0,
            notes: source.notes || "",
            createdAt: Date.now(),
          },
        };
      });

    if (candidates.length === 0) {
      setCopyError("Inga inlästa batcher hittades i intervallet. Ladda äldre batcher i listan om det behövs.");
      return;
    }
    setCopyError("");
    setCopyCandidates(candidates);
    setCopyOpen(false);
    setCopyConfirmOpen(true);
  };

  const handleConfirmCopy = async () => {
    const selectedCandidates = copyCandidates.filter((candidate) => candidate.selected);
    if (selectedCandidates.length === 0) {
      setCopyError("Välj minst en batch att kopiera.");
      return;
    }
    setCopyError("");
    setCopyLoading(true);
    try {
      const batchCollection = collection(firebaseStore, "products_fresh_batch");
      for (const candidate of selectedCandidates) {
        const created = await addDoc(batchCollection, candidate.copied);
        syncBatchLocally({ ...candidate.copied, firebaseId: created.id });
        setCopyCandidates((items) => items.filter((item) => item.copied.batchId !== candidate.copied.batchId));
      }
      clearFilters();
      Array.from(new Set(selectedCandidates.map((candidate) => getFreshBatchStatus(candidate.copied)))).forEach(openStatus);
      setCopyConfirmOpen(false);
      setCopyCandidates([]);
    } catch (error) {
      console.error("Error copying fresh batches:", error);
      setCopyError("Kopieringen avbröts. Redan skapade kopior har tagits bort från urvalet. Försök igen med de återstående batcherna.");
    } finally {
      setCopyLoading(false);
    }
  };

  // --- Single batch helpers ---

  const resetSingleForm = () => {
    setSingleError("");
    setSingleSelectedProduct(null);
    setSingleSeedBagNumber("");
    setSinglePlantDate("");
    setSinglePlantedBy("");
    setSingleHarvestDate("");
    setSingleHarvestedBy("");
    setSingleTrayCount(null);
    setSingleFreshWeight(null);
    setSingleNotes("");
    setSingleException(false);
    setSingleExceptionNote("");
    setSingleTraysLeft(null);
    setSingleCanceled(false);
    setEditingBatch(null);
  };

  const handleOpenAdd = () => {
    resetSingleForm();
    setOpen(true);
  };

  const handleOpenEdit = (batch: FreshProductBatch) => {
    setSingleError("");
    setEditingBatch(batch);
    const product = products.find((p) => p.id === batch.productId);
    setSingleSelectedProduct(product || null);
    setSingleSeedBagNumber(batch.seedBagNumber || "");
    setSinglePlantDate(batch.plantDate ? moment(batch.plantDate).format("YYYY-MM-DD") : "");
    setSinglePlantedBy(batch.plantedBy || "");
    setSingleHarvestDate(batch.harvestDate ? moment(batch.harvestDate).format("YYYY-MM-DD") : "");
    setSingleHarvestedBy(batch.harvestedBy || "");
    setSingleTrayCount(batch.trayCount);
    setSingleFreshWeight(batch.freshWeight);
    setSingleNotes(batch.notes || "");
    setSingleException(batch.exception === true);
    setSingleExceptionNote(batch.exceptionNote || "");
    setSingleTraysLeft(batch.traysLeft ?? null);
    setSingleCanceled(batch.canceled === true);
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  const handleSaveSingle = async () => {
    if (!singleSelectedProduct) { setSingleError("Välj en produkt."); return; }
    if (singlePlantDate && singleHarvestDate && singleHarvestDate < singlePlantDate) {
      setSingleError("Skördedatum måste vara samma som eller efter planteringsdatum."); return;
    }
    if ([singleTrayCount, singleFreshWeight, singleTraysLeft].some((value) => value != null && value < 0)) {
      setSingleError("Antal brickor och vikt kan inte vara negativa."); return;
    }
    setSingleError("");
    setDialogLoading(true);

    const batchId = editingBatch?.batchId || (() => {
      const sanitized = singleSelectedProduct.name_sv
        .normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^A-Za-z0-9]/g, "");
      const prefix = sanitized.slice(0, 3).toUpperCase();
      const dateStr = singleHarvestDate
        ? moment(singleHarvestDate).format("YYMMDD")
        : moment().format("YYMMDD");
      return `${prefix}-${dateStr}-${nanoid(4).toUpperCase()}`;
    })();

    const batchData = {
      productId: singleSelectedProduct.id,
      batchId,
      seedBagNumber: singleSeedBagNumber.trim(),
      plantDate: singlePlantDate ? new Date(singlePlantDate).getTime() : null,
      plantedBy: singlePlantedBy.trim(),
      harvestDate: singleHarvestDate ? new Date(singleHarvestDate).getTime() : null,
      harvestedBy: singleHarvestedBy.trim(),
      trayCount: singleTrayCount || 0,
      freshWeight: singleFreshWeight || 0,
      notes: singleNotes.trim(),
      exception: !singleCanceled && singleException,
      exceptionNote: singleExceptionNote.trim(),
      traysLeft: singleCanceled ? 0 : singleTraysLeft ?? null,
      canceled: singleCanceled,
      canceledAt: singleCanceled ? editingBatch?.canceledAt || Date.now() : null,
      canceledBy: singleCanceled ? editingBatch?.canceledBy || user?.email || "" : "",
    };

    try {
      if (editingBatch) {
        await updateDoc(doc(firebaseStore, "products_fresh_batch", editingBatch.firebaseId), batchData);
        showSavedBatch({ ...editingBatch, ...batchData });
      } else {
        const createdAt = Date.now();
        const created = await addDoc(collection(firebaseStore, "products_fresh_batch"), { ...batchData, createdAt });
        showSavedBatch({ ...batchData, createdAt, firebaseId: created.id });
      }
      handleClose();
    } catch (error) {
      console.error("Error saving batch:", error);
      setSingleError("Batchen kunde inte sparas. Dina uppgifter finns kvar, försök igen.");
    }
    setDialogLoading(false);
  };

  const handleConfirmAction = async () => {
    if (!batchAction) return;
    const { batch, kind } = batchAction;
    setActionLoading(true);
    setActionError("");
    try {
      const reference = doc(firebaseStore, "products_fresh_batch", batch.firebaseId);
      if (kind === "delete") {
        await deleteDoc(reference);
        setBatchesByStatus((prev) => Object.fromEntries(FRESH_BATCH_STATUSES.map((status) => [
          status, prev[status].filter((item) => item.firebaseId !== batch.firebaseId),
        ])) as Record<FreshBatchStatus, FreshProductBatch[]>);
      } else {
        const changes = kind === "cancel" ? {
          canceled: true, canceledAt: Date.now(), canceledBy: user?.email || "",
          exception: false, exceptionNote: batch.exceptionNote || "", traysLeft: 0,
        } : {
          canceled: false, canceledAt: null, canceledBy: "",
          traysLeft: batch.traysLeft ?? batch.trayCount ?? null,
        };
        await updateDoc(reference, changes);
        showSavedBatch({ ...batch, ...changes });
      }
      setActionOpen(false);
    } catch (error) {
      console.error("Error updating batch:", error);
      setActionError("Åtgärden kunde inte slutföras. Försök igen.");
    } finally {
      setActionLoading(false);
    }
  };

  const openBatchAction = (kind: "cancel" | "restore" | "delete", batch: FreshProductBatch) => {
    setActionError("");
    setBatchAction({ kind, batch });
    setActionOpen(true);
  };

  // --- Multi-batch helpers ---

  const resetMultiForm = () => {
    setMultiError("");
    setProductSearch("");
    setMultiPlantDate("");
    setMultiPlantedBy("");
    setMultiHarvestDate("");
    setMultiHarvestedBy("");
    setSelectedProducts([]);
  };

  const handleOpenMultiAdd = () => {
    resetMultiForm();
    setMultiOpen(true);
  };

  const handleCloseMulti = () => setMultiOpen(false);

  const handleToggleProduct = (productId: string) => {
    setSelectedProducts((prev) => {
      const exists = prev.find((p) => p.productId === productId);
      if (exists) {
        return prev.filter((p) => p.productId !== productId);
      }
      return [...prev, { productId, trayCount: 0 }];
    });
  };

  const handleMultiTrayChange = (productId: string, value: number) => {
    setSelectedProducts((prev) =>
      prev.map((p) => (p.productId === productId ? { ...p, trayCount: value } : p))
    );
  };

  const handleSaveMulti = async () => {
    if (selectedProducts.length === 0) { setMultiError("Välj minst en produkt."); return; }
    if (!multiPlantDate && !multiHarvestDate) { setMultiError("Ange planteringsdatum eller skördedatum."); return; }

    if (multiPlantDate && multiHarvestDate && multiHarvestDate < multiPlantDate) {
      setMultiError("Skördedatum måste vara samma som eller efter planteringsdatum."); return;
    }
    if (selectedProducts.some((entry) => entry.trayCount < 0)) {
      setMultiError("Antal brickor kan inte vara negativt."); return;
    }
    setMultiError("");
    setMultiDialogLoading(true);

    const plantTimestamp = multiPlantDate ? new Date(multiPlantDate).getTime() : null;
    const harvestTimestamp = multiHarvestDate ? new Date(multiHarvestDate).getTime() : null;

    const batchDocs = selectedProducts.map((entry) => {
      const product = products.find((p) => p.id === entry.productId);
      const name = product?.name_sv || entry.productId;
      const sanitized = name.normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^A-Za-z0-9]/g, "");
      const prefix = sanitized.slice(0, 3).toUpperCase();
      const dateStr = harvestTimestamp
        ? moment(harvestTimestamp).format("YYMMDD")
        : plantTimestamp
          ? moment(plantTimestamp).format("YYMMDD")
          : moment().format("YYMMDD");
      const batchId = `${prefix}-${dateStr}-${nanoid(4).toUpperCase()}`;

      return {
        productId: entry.productId,
        batchId,
        plantDate: plantTimestamp,
        plantedBy: multiPlantedBy.trim(),
        harvestDate: harvestTimestamp,
        harvestedBy: multiHarvestedBy.trim(),
        trayCount: entry.trayCount || 0,
        freshWeight: 0,
        notes: "",
        createdAt: Date.now(),
      };
    });

    try {
      const batchCollection = collection(firebaseStore, "products_fresh_batch");
      for (const docData of batchDocs) {
        const created = await addDoc(batchCollection, docData);
        syncBatchLocally({ ...docData, firebaseId: created.id });
        // Keep only unfinished products selected if a later write fails.
        setSelectedProducts((items) => items.filter((item) => item.productId !== docData.productId));
      }
      setSearch("");
      selectStatus(getFreshBatchStatus(batchDocs[0]));
      handleCloseMulti();
    } catch (error) {
      console.error("Error saving multi-batch:", error);
      setMultiError("Alla batcher kunde inte skapas. Bara återstående produkter är valda. Försök igen.");
    }
    setMultiDialogLoading(false);
  };

  const hasFilters = !!searchTerm || statusFilter !== "all";
  const plantListResultIsCurrent = plantListResult?.startDate === plantListStartDate && plantListResult?.endDate === plantListEndDate;
  const plantListBatches = plantListResultIsCurrent ? sortPlantListBatches(plantListResult!.batches) : [];
  const plantListRangeInvalid = !!plantListStartDate && !!plantListEndDate && plantListStartDate > plantListEndDate;
  const copyRangeInvalid = !!copyStartDate && !!copyEndDate && copyStartDate > copyEndDate;
  const selectedCopies = copyCandidates.filter((candidate) => candidate.selected);
  const filteredProducts = products.filter((product) => `${product.name_sv} ${product.name_en}`.toLocaleLowerCase("sv-SE").includes(productSearch.trim().toLocaleLowerCase("sv-SE")));

  return (
    <div className="DashBoardTabInner FreshDashboardTab BatchWorkspace FreshBatchWorkspace">
      <div className="FreshTabToolbar">
        <div>
          <p className="FreshEyebrow">Produktion</p>
          <h2 className="FreshTabTitle">Färska batcher</h2>
          <p className="FreshTabSummary">Följ dina odlingar från sådd till skörd.</p>
        </div>
        <div className="FreshBatchPrimaryActions">
          <IconButton title="Om Färska batcher" aria-label="Om Färska batcher" onClick={() => setInfoDialogOpen(true)}><InfoOutlinedIcon /></IconButton>
          <Button color="inherit" variant="contained" startIcon={<AddIcon />} onClick={handleOpenAdd}>Ny batch</Button>
        </div>
      </div>

      <div className="FreshBatchSecondaryActions" role="group" aria-label="Batchverktyg">
        <Button color="inherit" variant="outlined" startIcon={<PlaylistAddIcon />} onClick={handleOpenMultiAdd}>Flera batcher</Button>
        <Button color="inherit" variant="outlined" startIcon={<ContentCopyIcon />} onClick={handleOpenCopy}>Kopiera batcher</Button>
        <Button color="inherit" variant="outlined" startIcon={<PictureAsPdfIcon />} onClick={handleOpenPlantList}>Plantlista PDF</Button>
      </div>

      <div className="BatchTools" role="search" aria-label="Filtrera färska batcher">
        <div className="BatchSearchRow">
          <TextField className="BatchSearch" label="Sök batcher" placeholder="Produkt, batch-ID, fröpåse, person…" size="small"
            value={search} onChange={(event) => setSearch(event.target.value)} inputProps={{ "aria-describedby": "fresh-batch-search-scope" }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
              endAdornment: search ? <InputAdornment position="end"><IconButton size="small" aria-label="Rensa sökning" onClick={() => setSearch("")}><CloseIcon fontSize="small" /></IconButton></InputAdornment> : undefined,
            }} />
          <TextField className="BatchSort" select label="Sortera" size="small" value={sortBy} onChange={(event) => setSortBy(event.target.value as typeof sortBy)}>
            <MenuItem value="created">Senast skapad</MenuItem>
            <MenuItem value="parent">Produkt A–Ö</MenuItem>
            <MenuItem value="planted">Sådd, äldst först</MenuItem>
            <MenuItem value="harvested">Skörd, äldst först</MenuItem>
          </TextField>
        </div>
        <div className="BatchFilterRow">
          <div className="BatchFilters" role="group" aria-label="Filtrera efter batchstatus">
            <button type="button" className="BatchFilter" aria-pressed={statusFilter === "all"} onClick={() => selectStatus("all")}>Alla<span className="BatchFilterCount">{allBatches.filter(matchesSearch).length}</span></button>
            {FRESH_BATCH_STATUSES.map((status) => (
              <button type="button" key={status} className={`BatchFilter BatchFilter-${status}`} aria-pressed={statusFilter === status}
                onClick={() => selectStatus(status)} title={STATUS_DESCRIPTIONS[status]}>
                <span className={`BatchStatusDot BatchStatusDot-${status}`} />{freshBatchStatusLabel[status]}
                <span className="BatchFilterCount">{loadedStatuses[status] && !loadingByStatus[status] ? batchesByStatus[status].filter(matchesSearch).length : "—"}</span>
              </button>
            ))}
          </div>
          {hasFilters && <Button color="inherit" size="small" onClick={clearFilters}>Rensa filter</Button>}
        </div>
        <p className="BatchScope" id="fresh-batch-search-scope">Sökning och antal gäller inlästa batcher. Välj en status eller ladda äldre batcher för att visa fler.</p>
      </div>

      <div className="BatchProcesses">
        {FRESH_BATCH_STATUSES.filter((status) => statusFilter === "all" || statusFilter === status).map((status) => {
          const isExpanded = expandedStatuses[status];
          const isLoading = loadingByStatus[status];
          const statusBatches = getSortedBatches(status).filter(matchesSearch);
          const next = getNextLoadStage(status);
          const stage = loadStageByStatus[status];
          const StatusIcon = STATUS_ICONS[status];
          return (
            <section className={`BatchProcess FreshBatchStatus-${status}`} key={status}>
              <h3 className="BatchProcessHeading">
                <button type="button" className="BatchProcessToggle" onClick={() => handleToggleGroup(status)}
                  aria-expanded={isExpanded} aria-controls={`fresh-batches-${status}`} id={`fresh-batches-heading-${status}`}>
                  <span className="BatchProcessIcon"><StatusIcon /></span>
                  <span className="BatchProcessTitle"><strong>{freshBatchStatusLabel[status]}</strong><span>{STATUS_DESCRIPTIONS[status]}</span></span>
                  <span className="BatchProcessCount">{!loadedStatuses[status] ? "Visa batcher" : isLoading ? "Laddar…" : batchCountLabel(statusBatches.length)}</span>
                  {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </button>
              </h3>
              <div id={`fresh-batches-${status}`} hidden={!isExpanded} aria-labelledby={`fresh-batches-heading-${status}`}>
                {isExpanded && <>
                  {loadErrors[status] && <Alert severity="error" className="BatchError" action={!unsubscribeRefs.current[status] ? <Button color="inherit" size="small" onClick={() => startGroupListener(status)}>Försök igen</Button> : undefined}>{loadErrors[status]}</Alert>}
                  {isLoading ? <div className="BatchEmpty" role="status"><CircularProgress color="inherit" size={24} /><p>Hämtar batcher…</p></div> : !statusBatches.length ? (
                    <div className="BatchEmpty" role="status">
                      <SearchIcon /><strong>{searchTerm ? "Inga batcher matchar sökningen" : "Inga batcher i den här perioden"}</strong>
                      <p>{searchTerm ? "Prova en annan sökning eller ladda äldre batcher." : "Här visas batcher med den här statusen. Ladda äldre batcher för att söka längre tillbaka."}</p>
                      {searchTerm && <Button color="inherit" size="small" onClick={() => setSearch("")}>Rensa sökning</Button>}
                    </div>
                  ) : (
                    <div className="BatchGrid FreshBatchCardGrid">
                      {statusBatches.map((batch) => {
                        const product = products.find((item) => item.id === batch.productId);
                        const hasDetails = batch.seedBagNumber || batch.notes || batch.canceledAt || batch.canceledBy;
                        return (
                          <article className="BatchCard" key={batch.firebaseId} aria-label={`${product?.name_sv || batch.productId}, ${batch.batchId}`}>
                            <div className="BatchCardHeader">
                              <div className="BatchProductImage">{product?.icon ? <img src={product.icon} alt="" loading="lazy" /> : <GrassIcon />}</div>
                              <div className="BatchIdentity"><h4>{product?.name_sv || batch.productId}</h4><span className="BatchId">{batch.batchId}</span></div>
                              <IconButton color="inherit" title="Redigera batch" aria-label={`Redigera batch ${batch.batchId}`} onClick={() => handleOpenEdit(batch)}><EditIcon fontSize="small" /></IconButton>
                            </div>
                            <dl className="BatchDates">
                              <div><dt>Sådd</dt><dd>{batch.plantDate ? <time dateTime={moment(batch.plantDate).format("YYYY-MM-DD")}>{moment(batch.plantDate).format("YYYY-MM-DD")}</time> : "Ej angiven"}</dd>{batch.plantedBy && <dd className="FreshBatchPerson">{batch.plantedBy}</dd>}</div>
                              <div><dt>Skörd</dt><dd>{batch.harvestDate ? <time dateTime={moment(batch.harvestDate).format("YYYY-MM-DD")}>{moment(batch.harvestDate).format("YYYY-MM-DD")}</time> : "Ej angiven"}</dd>{batch.harvestedBy && <dd className="FreshBatchPerson">{batch.harvestedBy}</dd>}</div>
                            </dl>
                            <dl className="BatchWeights">
                              <div><dt>Brickor</dt><dd>{batch.trayCount ?? "—"}</dd></div>
                              {(batch.exception || batch.canceled) && <div><dt>Brickor kvar</dt><dd>{batch.canceled ? 0 : batch.traysLeft ?? batch.trayCount ?? "—"}</dd></div>}
                              <div><dt>Färskvikt</dt><dd>{batch.freshWeight == null ? "—" : `${batch.freshWeight.toLocaleString("sv-SE")} g`}</dd></div>
                            </dl>
                            {batch.exception && !batch.canceled && <div className="FreshBatchIssue"><WarningAmberIcon fontSize="small" /><span><strong>Avvikelse</strong>{batch.exceptionNote || "Ingen beskrivning angiven."}</span></div>}
                            <div className="BatchCardFooter">
                              {hasDetails ? <details className="BatchDetails"><summary>Batchdetaljer</summary><dl>
                                {batch.seedBagNumber && <div><dt>Fröpåse</dt><dd>{batch.seedBagNumber}</dd></div>}
                                {batch.notes && <div><dt>Anteckningar</dt><dd>{batch.notes}</dd></div>}
                                {batch.canceledAt && <div><dt>Avbruten</dt><dd>{moment(batch.canceledAt).format("YYYY-MM-DD")}</dd></div>}
                                {batch.canceledBy && <div><dt>Avbruten av</dt><dd>{batch.canceledBy}</dd></div>}
                              </dl></details> : <span className="BatchNoDetails">Inga extra uppgifter</span>}
                              <div className="FreshBatchCardActions">
                                <IconButton color="inherit" title={batch.canceled ? "Återställ batch" : "Avbryt batch"} aria-label={`${batch.canceled ? "Återställ" : "Avbryt"} batch ${batch.batchId}`} onClick={() => openBatchAction(batch.canceled ? "restore" : "cancel", batch)}>{batch.canceled ? <RestoreIcon fontSize="small" /> : <CancelIcon fontSize="small" />}</IconButton>
                                <IconButton className="BatchDelete" color="inherit" title="Ta bort batch" aria-label={`Ta bort batch ${batch.batchId}`} onClick={() => openBatchAction("delete", batch)}><DeleteIcon fontSize="small" /></IconButton>
                              </div>
                            </div>
                          </article>
                        );
                      })}
                    </div>
                  )}
                  {!isLoading && <div className="BatchLoadMore">
                    <p><HistoryIcon fontSize="small" /><span>{next ? `Inläst: ${stage === 0 ? "senaste 2 veckorna" : LOAD_STAGES[stage - 1].label}` : "All historik är inläst"}<small>{statusBatches.length} av {batchesByStatus[status].length} inlästa batcher visas</small></span></p>
                    {next && <Button color="inherit" variant="outlined" size="small" onClick={() => handleLoadMore(status)} disabled={loadingMoreByStatus[status]} startIcon={loadingMoreByStatus[status] ? <CircularProgress size={14} color="inherit" /> : <HistoryIcon />}>
                      {loadingMoreByStatus[status] ? "Laddar äldre batcher…" : `Ladda ${next.isLast ? "all historik" : next.label}`}
                    </Button>}
                  </div>}
                </>}
              </div>
            </section>
          );
        })}
      </div>

      <FreshBatchDialog id="fresh-batch-info" open={infoDialogOpen} onClose={() => setInfoDialogOpen(false)} title="Om Färska batcher"
        description="Registrera odlingsomgångar, följ skörden och planera nästa period."
        actions={<Button color="inherit" variant="contained" onClick={() => setInfoDialogOpen(false)}>Stäng</Button>}>
        <div className="FreshBatchForm">
          <BatchFormSection title="Från sådd till skörd">
            <ol className="FreshBatchHelpSteps">
              <li><strong>Registrera en odling</strong><span>Välj Ny batch, eller Flera batcher för flera produkter med gemensamma datum.</span></li>
              <li><strong>Följ upp och komplettera</strong><span>Redigera batchen när du vet skördedatum, färskvikt eller vill lägga till anteckningar.</span></li>
              <li><strong>Planera nästa period</strong><span>Exportera en plantlista som PDF eller kopiera batcher till nya datum.</span></li>
            </ol>
          </BatchFormSection>
          <BatchFormSection title="Så fungerar statusarna">
            <dl className="FreshBatchStatusGuide">{FRESH_BATCH_STATUSES.map((status) => <div key={status}><dt><span className={`BatchStatusDot BatchStatusDot-${status}`} />{freshBatchStatusLabel[status]}</dt><dd>{STATUS_DESCRIPTIONS[status]}.</dd></div>)}</dl>
            <p className="FreshBatchHelpText">Markera avvikelse om exempelvis några brickor har kasserats. Ange antal brickor kvar. Avbryt batchen om hela odlingen har gått förlorad.</p>
          </BatchFormSection>
          <Alert severity="info">Listan börjar med de senaste två veckorna. Ladda äldre batcher innan du söker eller kopierar historik. Plantlistan hämtar automatiskt alla batcher i den valda perioden.</Alert>
        </div>
      </FreshBatchDialog>

      <FreshBatchDialog id="fresh-batch-edit" open={open} onClose={handleClose} title={editingBatch ? "Redigera batch" : "Ny färsk batch"}
        description={editingBatch ? editingBatch.batchId : "Välj produkt och fyll i det du vet. Du kan komplettera med skörden senare."}
        busy={dialogLoading} error={singleError} wide
        actions={<><Button color="inherit" onClick={handleClose} disabled={dialogLoading}>Avbryt</Button><Button color="inherit" variant="contained" onClick={handleSaveSingle} disabled={dialogLoading || !singleSelectedProduct}>{dialogLoading ? "Sparar…" : editingBatch ? "Spara ändringar" : "Skapa batch"}</Button></>}>
        <div className="FreshBatchForm">
          <BatchFormSection title="Produkt och spårbarhet">
            <div className="FreshBatchFormGrid">
              <Autocomplete options={products} getOptionLabel={(product) => product.name_sv} isOptionEqualToValue={(a, b) => a.id === b.id}
                value={singleSelectedProduct} onChange={(_event, value) => setSingleSelectedProduct(value)} disabled={dialogLoading}
                noOptionsText="Inga produkter hittades" renderInput={(params) => <TextField {...params} label="Produkt *" size="small" fullWidth />} />
              <TextField label="Fröpåsens batchnummer" size="small" fullWidth value={singleSeedBagNumber} onChange={(event) => setSingleSeedBagNumber(event.target.value)} disabled={dialogLoading} helperText="Fyll i när du öppnar en ny fröpåse." />
            </div>
          </BatchFormSection>
          <BatchFormSection title="Sådd och skörd">
            <div className="FreshBatchFormGrid">
              <BatchDateField label="Planteringsdatum" value={singlePlantDate} onChange={setSinglePlantDate} disabled={dialogLoading} />
              <BatchPersonField label="Planterad av" value={singlePlantedBy} onChange={setSinglePlantedBy} disabled={dialogLoading} />
              <BatchDateField label="Skördedatum" value={singleHarvestDate} onChange={setSingleHarvestDate} disabled={dialogLoading} />
              <BatchPersonField label="Skördad av" value={singleHarvestedBy} onChange={setSingleHarvestedBy} disabled={dialogLoading} />
            </div>
          </BatchFormSection>
          <BatchFormSection title="Mängd och anteckningar">
            <div className="FreshBatchFormGrid">
              <TextField label="Antal brickor" type="number" size="small" fullWidth value={singleTrayCount ?? ""} onChange={(event) => setSingleTrayCount(event.target.value ? Number(event.target.value) : null)} disabled={dialogLoading} inputProps={{ min: 0, step: 1 }} />
              <TextField label="Färskvikt (g)" type="number" size="small" fullWidth value={singleFreshWeight ?? ""} onChange={(event) => setSingleFreshWeight(event.target.value ? Number(event.target.value) : null)} disabled={dialogLoading} inputProps={{ min: 0 }} />
              <TextField className="FreshBatchFullWidth" label="Anteckningar" multiline minRows={2} size="small" fullWidth value={singleNotes} onChange={(event) => setSingleNotes(event.target.value)} disabled={dialogLoading} />
            </div>
          </BatchFormSection>
          {editingBatch && <BatchFormSection title="Status och uppföljning">
            <div className="FreshBatchStatusOptions">
              <FormControlLabel control={<Checkbox color="default" checked={singleException} onChange={(event) => setSingleException(event.target.checked)} disabled={dialogLoading || singleCanceled} />} label="Markera avvikelse" />
              <FormControlLabel control={<Checkbox color="default" checked={singleCanceled} onChange={(event) => setSingleCanceled(event.target.checked)} disabled={dialogLoading} />} label="Markera som avbruten" />
            </div>
            {singleException && !singleCanceled && <div className="FreshBatchFormGrid FreshBatchExceptionForm">
              <TextField label="Brickor kvar" type="number" size="small" fullWidth value={singleTraysLeft ?? ""} onChange={(event) => setSingleTraysLeft(event.target.value ? Number(event.target.value) : null)} disabled={dialogLoading} inputProps={{ min: 0, max: singleTrayCount ?? undefined }} helperText={singleTrayCount != null ? `Av ${singleTrayCount} brickor` : undefined} />
              <TextField label="Vad hände?" multiline minRows={2} size="small" fullWidth value={singleExceptionNote} onChange={(event) => setSingleExceptionNote(event.target.value)} disabled={dialogLoading} placeholder="T.ex. två brickor kasserades." />
            </div>}
            {singleCanceled ? <Alert severity="warning">Batchen flyttas till Avbrutna och behålls för spårbarhet.</Alert> : <p className="FreshBatchHelpText">Använd avvikelse om en del av odlingen påverkats. Avbryt batchen om alla brickor är borta.</p>}
          </BatchFormSection>}
        </div>
      </FreshBatchDialog>

      <FreshBatchDialog id="fresh-batch-multi" open={multiOpen} onClose={handleCloseMulti} title="Skapa flera batcher"
        description="Välj produkter och antal brickor. Datum och personer gäller för alla valda produkter."
        busy={multiDialogLoading} error={multiError} wide
        actions={<><Button color="inherit" onClick={handleCloseMulti} disabled={multiDialogLoading}>Avbryt</Button><Button color="inherit" variant="contained" onClick={handleSaveMulti} disabled={multiDialogLoading || !selectedProducts.length}>{multiDialogLoading ? "Skapar batcher…" : `Skapa ${batchCountLabel(selectedProducts.length)}`}</Button></>}>
        <div className="FreshBatchForm">
          <BatchFormSection title="Gemensamma datum och personer">
            <div className="FreshBatchFormGrid">
              <BatchDateField label="Planteringsdatum" value={multiPlantDate} onChange={setMultiPlantDate} disabled={multiDialogLoading} />
              <BatchPersonField label="Planterad av" value={multiPlantedBy} onChange={setMultiPlantedBy} disabled={multiDialogLoading} />
              <BatchDateField label="Skördedatum" value={multiHarvestDate} onChange={setMultiHarvestDate} disabled={multiDialogLoading} />
              <BatchPersonField label="Skördad av" value={multiHarvestedBy} onChange={setMultiHarvestedBy} disabled={multiDialogLoading} />
            </div>
            <p className="FreshBatchHelpText">Ange minst planteringsdatum eller skördedatum.</p>
          </BatchFormSection>
          <BatchFormSection title="Produkter och brickor">
            <TextField label="Sök produkter" placeholder="Produktnamn…" size="small" fullWidth value={productSearch} onChange={(event) => setProductSearch(event.target.value)} disabled={multiDialogLoading}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
            <div className="FreshBatchSelectionSummary" role="status"><strong>{selectedProducts.length} {selectedProducts.length === 1 ? "produkt vald" : "produkter valda"}</strong><span>{selectedProducts.reduce((total, entry) => total + entry.trayCount, 0)} brickor totalt</span></div>
            <div className="FreshBatchProductPicker">
              {!filteredProducts.length && <p className="FreshBatchHelpText">{products.length ? "Inga produkter matchar sökningen." : "Inga produkter finns ännu. Lägg till dem under Färska produkter."}</p>}
              {filteredProducts.map((product) => {
                const selected = selectedProducts.find((entry) => entry.productId === product.id);
                return <div className={`FreshBatchProductRow${selected ? " isSelected" : ""}`} key={product.id}>
                  <FormControlLabel control={<Checkbox color="default" checked={!!selected} onChange={() => handleToggleProduct(product.id)} disabled={multiDialogLoading} />}
                    label={<span><strong>{product.name_sv}</strong>{product.name_en && <small>{product.name_en}</small>}</span>} />
                  {selected && <TextField label="Brickor" type="number" size="small" value={selected.trayCount || ""} onChange={(event) => handleMultiTrayChange(product.id, event.target.value ? Number(event.target.value) : 0)} disabled={multiDialogLoading} inputProps={{ min: 0, step: 1, "aria-label": `Brickor för ${product.name_sv}` }} />}
                </div>;
              })}
            </div>
          </BatchFormSection>
        </div>
      </FreshBatchDialog>

      <FreshBatchDialog id="fresh-batch-plantlist" open={plantListOpen} onClose={() => setPlantListOpen(false)} title="Exportera plantlista"
        description="Välj planteringsdatum och kontrollera urvalet innan du skapar din PDF."
        busy={plantListLoading} error={plantListError}
        actions={<><Button color="inherit" onClick={() => setPlantListOpen(false)} disabled={plantListLoading}>Avbryt</Button><Button color="inherit" variant="contained" startIcon={<PictureAsPdfIcon />} onClick={handleExportPlantList} disabled={plantListLoading || plantListFetching || !plantListStartDate || !plantListEndDate || plantListRangeInvalid || !plantListResultIsCurrent || !plantListBatches.length}>{plantListLoading ? "Skapar PDF…" : "Exportera PDF"}</Button></>}>
        <div className="FreshBatchForm">
          <BatchFormSection title="Planteringsperiod">
            <div className="FreshBatchFormGrid">
              <BatchDateField label="Från och med" value={plantListStartDate} onChange={setPlantListStartDate} disabled={plantListLoading} />
              <BatchDateField label="Till och med" value={plantListEndDate} onChange={setPlantListEndDate} disabled={plantListLoading} error={plantListRangeInvalid} helperText={plantListRangeInvalid ? "Välj samma eller ett senare datum." : undefined} />
            </div>
          </BatchFormSection>
          <div>
            <div className="FreshBatchSelectionSummary" role="status"><strong>{plantListFetching ? "Hämtar batcher för perioden…" : plantListResultIsCurrent ? `${batchCountLabel(plantListBatches.length)} i urvalet` : plantListError ? "Urvalet kunde inte hämtas" : "Välj planteringsperiod"}</strong>{plantListResultIsCurrent && <span>{plantListBatches.reduce((total, batch) => total + (batch.trayCount || 0), 0)} brickor</span>}</div>
            <div className="FreshBatchPreview" role="region" aria-label="Batcher i plantlistan" aria-busy={plantListFetching} tabIndex={0}>
              {plantListFetching ? <p className="FreshBatchPreviewEmpty"><CircularProgress size={20} color="inherit" aria-label="Hämtar batcher till plantlistan" /></p> : plantListBatches.length ? plantListBatches.map((batch) => <div className="FreshBatchPreviewRow" key={batch.firebaseId}>
                <div><strong>{products.find((product) => product.id === batch.productId)?.name_sv || batch.productId}</strong><span>{batch.batchId}</span><small>{freshBatchStatusLabel[getFreshBatchStatus(batch)]}</small></div>
                <div><span>{moment(batch.plantDate).format("YYYY-MM-DD")}</span><small>{batch.trayCount ?? "—"} brickor</small></div>
              </div>) : <p className="FreshBatchPreviewEmpty">{plantListResultIsCurrent ? "Inga batcher matchar perioden." : plantListError ? "Batcherna kunde inte hämtas." : "Välj ett giltigt datumintervall."}</p>}
            </div>
            {plantListError && !plantListResultIsCurrent && !plantListFetching && <Button color="inherit" onClick={() => setPlantListReload((attempt) => attempt + 1)}>Försök igen</Button>}
          </div>
          <Alert severity="info">Alla batcher med planteringsdatum inom perioden hämtas automatiskt, inklusive avbrutna och avvikelser.</Alert>
        </div>
      </FreshBatchDialog>

      <FreshBatchDialog id="fresh-batch-copy" open={copyOpen} onClose={() => setCopyOpen(false)} title="Kopiera batcher"
        description="Steg 1 av 2 · Välj en tidigare odlingsperiod och flytta den till nya datum."
        error={copyError}
        actions={<><Button color="inherit" onClick={() => setCopyOpen(false)}>Avbryt</Button><Button color="inherit" variant="contained" endIcon={<ArrowForwardIcon />} onClick={handlePrepareCopy} disabled={!copyStartDate || !copyEndDate || !copyPasteStartDate || copyRangeInvalid}>Granska batcher</Button></>}>
        <div className="FreshBatchForm">
          <BatchFormSection title="Period att kopiera">
            <div className="FreshBatchFormGrid">
              <BatchDateField label="Kopiera från och med" value={copyStartDate} onChange={setCopyStartDate} />
              <BatchDateField label="Kopiera till och med" value={copyEndDate} onChange={setCopyEndDate} error={copyRangeInvalid} helperText={copyRangeInvalid ? "Välj samma eller ett senare datum." : undefined} />
            </div>
          </BatchFormSection>
          <BatchFormSection title="Ny odlingsperiod">
            <BatchDateField label="Ny periods startdatum" value={copyPasteStartDate} onChange={setCopyPasteStartDate} />
            <p className="FreshBatchHelpText">Avståndet mellan planteringsdagarna behålls. Skördedatumen flyttas lika många dagar.</p>
          </BatchFormSection>
          <Alert severity="info">Kopieringen utgår från inlästa batcher. Öppna rätt status och ladda önskad historik i listan innan du väljer period.</Alert>
        </div>
      </FreshBatchDialog>

      <FreshBatchDialog id="fresh-batch-copy-confirm" open={copyConfirmOpen} onClose={() => setCopyConfirmOpen(false)} title="Granska nya batcher"
        description="Steg 2 av 2 · Välj vilka batcher som ska skapas och kontrollera deras nya datum."
        busy={copyLoading} error={copyError} wide
        actions={<><Button color="inherit" onClick={() => { setCopyError(""); setCopyConfirmOpen(false); setCopyOpen(true); }} disabled={copyLoading}>Tillbaka</Button><Button color="inherit" variant="contained" startIcon={<ContentCopyIcon />} onClick={handleConfirmCopy} disabled={copyLoading || !selectedCopies.length}>{copyLoading ? "Kopierar…" : `Kopiera ${batchCountLabel(selectedCopies.length)}`}</Button></>}>
        <div className="FreshBatchForm">
          <div className="FreshBatchSelectionSummary">
            <FormControlLabel control={<Checkbox color="default" checked={!!copyCandidates.length && selectedCopies.length === copyCandidates.length} indeterminate={selectedCopies.length > 0 && selectedCopies.length < copyCandidates.length} onChange={(event) => setCopyCandidates((items) => items.map((item) => ({ ...item, selected: event.target.checked })))} disabled={copyLoading} />} label={`${selectedCopies.length} av ${copyCandidates.length} valda`} />
            <span>{selectedCopies.reduce((sum, candidate) => sum + (candidate.copied.trayCount || 0), 0)} brickor</span>
          </div>
          <div className="FreshBatchCopyReview">
            {copyCandidates.map((candidate, index) => <div className={`FreshBatchCopyRow${candidate.selected ? " isSelected" : ""}`} key={candidate.copied.batchId}>
              <Checkbox color="default" checked={candidate.selected} onChange={(event) => setCopyCandidates((items) => items.map((item, itemIndex) => itemIndex === index ? { ...item, selected: event.target.checked } : item))} disabled={copyLoading} inputProps={{ "aria-label": `Kopiera ${candidate.copied.batchId}` }} />
              <div className="FreshBatchCopyContent">
                <strong>{products.find((product) => product.id === candidate.copied.productId)?.name_sv || candidate.copied.productId}</strong>
                <span className="BatchId">Nytt batch-ID: {candidate.copied.batchId}</span>
                <div className="FreshBatchDateShift"><span><small>Tidigare sådd</small>{moment(candidate.source.plantDate).format("YYYY-MM-DD")}</span><ArrowForwardIcon fontSize="small" /><span><small>Ny sådd</small>{moment(candidate.copied.plantDate).format("YYYY-MM-DD")}</span></div>
                <div className="FreshBatchCopyMeta"><span>Ny skörd: {candidate.copied.harvestDate ? moment(candidate.copied.harvestDate).format("YYYY-MM-DD") : "Ej angiven"}</span><span>{candidate.copied.trayCount ?? 0} brickor</span>{candidate.copied.seedBagNumber && <span>Fröpåse: {candidate.copied.seedBagNumber}</span>}</div>
              </div>
            </div>)}
          </div>
          <p className="FreshBatchHelpText">Fröpåse, personer, vikt och anteckningar följer med. Kopior får nya batch-ID:n. Avbrott och avvikelser följer inte med.</p>
        </div>
      </FreshBatchDialog>

      <FreshBatchDialog id="fresh-batch-action" open={actionOpen} onClose={() => setActionOpen(false)}
        title={batchAction?.kind === "delete" ? "Ta bort batch?" : batchAction?.kind === "restore" ? "Återställ batch?" : "Avbryt batch?"}
        description={batchAction?.kind === "delete" ? "Batchen tas bort permanent. Åtgärden går inte att ångra." : batchAction?.kind === "restore" ? `Batchen återställs och flyttas till ${freshBatchStatusLabel[getFreshBatchStatus({ ...batchAction.batch, canceled: false })]}.` : "Batchen markeras som avbruten och behålls för spårbarhet."}
        busy={actionLoading} error={actionError}
        actions={<><Button color="inherit" onClick={() => setActionOpen(false)} disabled={actionLoading}>Behåll som den är</Button><Button className={batchAction?.kind === "restore" ? "" : "FreshBatchDestructiveButton"} color="inherit" variant="contained" onClick={handleConfirmAction} disabled={actionLoading}>{actionLoading ? "Sparar…" : batchAction?.kind === "delete" ? "Ta bort batch" : batchAction?.kind === "restore" ? "Återställ batch" : "Avbryt batch"}</Button></>}>
        {batchAction && <div className="FreshBatchActionSummary"><GrassIcon /><div><strong>{getProductName(batchAction.batch.productId)}</strong><span>{batchAction.batch.batchId}</span><small>{batchAction.batch.trayCount ?? "—"} brickor · {freshBatchStatusLabel[getFreshBatchStatus(batchAction.batch)]}</small></div></div>}
      </FreshBatchDialog>
    </div>
  );
}

export default DashBoardFreshBatches;
